import { useCallback, useEffect, useState, useRef } from "react";
import ExoDialog from "./ExoDialog";
import { useResizeObserver } from "@wojtekmaj/react-hooks";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import {
  Box,
  IconButton,
  useTheme,
  Skeleton,
  Typography,
  Link,
  TextField,
  useMediaQuery,
  Drawer,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { tokens } from "../../global/theme/tokens";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import NoDataAnimation from "../animation/NoDataAnimation";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const resizeObserverOptions = {};

// Thumbnail-Sidebar Komponente
const PDFThumbnailSidebar = ({
  numPages,
  file,
  currentPage,
  onPageSelect,
  colors,
}) => {
  return (
    <Box
      className="flex flex-col gap-2 p-2 overflow-y-auto"
      sx={{
        width: 120,
        bgcolor: colors.primary[400],
        borderRight: `1px solid ${colors.primary[300]}`,
      }}
    >
      {Array.from(new Array(numPages || 0), (el, index) => (
        <Box
          key={`thumb_${index + 1}`}
          onClick={() => onPageSelect(index + 1)}
          className="cursor-pointer transition-all"
          sx={{
            border:
              currentPage === index + 1
                ? `2px solid ${colors.selected}`
                : `1px solid ${colors.primary[300]}`,
            borderRadius: 1,
            overflow: "hidden",
            opacity: currentPage === index + 1 ? 1 : 0.7,
            "&:hover": {
              opacity: 1,
              borderColor: colors.selected,
            },
          }}
        >
          <Document file={file} options={options} loading={null}>
            <Page
              pageNumber={index + 1}
              width={100}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
          <Typography
            variant="caption"
            className="text-center block"
            sx={{ py: 0.5, bgcolor: colors.primary[500] }}
          >
            {index + 1}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const ExoPDFViewer = ({
  src,
  open,
  onClose,
  type = "url",
  maxWidth = 2000,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const containerRef = useRef(null);
  const pageRefs = useRef({});

  const [file, setFile] = useState(src);
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Touch/Pinch-Zoom State - useRef statt useState um Re-Renders zu vermeiden
  const touchStateRef = useRef({
    initialDistance: null,
    initialScale: 1,
    isPinching: false,
  });

  useEffect(() => {
    setFile(src);
    setCurrentPage(1);
    setPageInput("1");
  }, [src]);

  const onResize = useCallback((entries) => {
    const [entry] = entries;
    if (entry) {
      setContainerWidth(entry.contentRect.width);
    }
  }, []);

  useResizeObserver(containerRef.current, resizeObserverOptions, onResize);

  function onDocumentLoadSuccess({ numPages: pages }) {
    setNumPages(pages);
  }

  // Zoom-Konfiguration
  const zoomInCap = 3;
  const zoomOutCap = 0.4;
  const scaleStepSize = 0.2;
  const [scale, setScale] = useState(1);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + scaleStepSize, zoomInCap));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - scaleStepSize, zoomOutCap));
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1);
  }, []);

  // Touch-Events für Pinch-to-Zoom
  const getDistance = (touches) => {
    const [touch1, touch2] = touches;
    return Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );
  };

  const handleTouchStart = useCallback(
    (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const distance = getDistance(e.touches);
        touchStateRef.current = {
          initialDistance: distance,
          initialScale: scale,
          isPinching: true,
        };
      }
    },
    [scale]
  );

  const handleTouchMove = useCallback((e) => {
    const touchState = touchStateRef.current;
    if (e.touches.length === 2 && touchState.isPinching && touchState.initialDistance) {
      e.preventDefault();
      const currentDistance = getDistance(e.touches);
      const scaleChange = currentDistance / touchState.initialDistance;
      const newScale = touchState.initialScale * scaleChange;
      // Nur updaten wenn sich der Wert signifikant ändert (Performance)
      const clampedScale = Math.min(Math.max(newScale, zoomOutCap), zoomInCap);
      setScale((prev) => {
        if (Math.abs(prev - clampedScale) > 0.01) {
          return clampedScale;
        }
        return prev;
      });
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    touchStateRef.current = {
      ...touchStateRef.current,
      initialDistance: null,
      isPinching: false,
    };
  }, []);

  // Seitennavigation
  const goToPage = useCallback(
    (page) => {
      const pageNum = Math.min(Math.max(1, page), numPages || 1);
      setCurrentPage(pageNum);
      setPageInput(String(pageNum));
      const pageElement = pageRefs.current[pageNum];
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [numPages]
  );

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e) => {
    if (e.key === "Enter") {
      const page = parseInt(pageInput, 10);
      if (!isNaN(page)) {
        goToPage(page);
      }
    }
  };

  const handlePageInputBlur = () => {
    const page = parseInt(pageInput, 10);
    if (!isNaN(page)) {
      goToPage(page);
    } else {
      setPageInput(String(currentPage));
    }
  };

  // Sidebar für Thumbnails (Mobile: Drawer, Desktop: inline)
  const thumbnailSidebar = numPages > 0 && (
    <PDFThumbnailSidebar
      numPages={numPages}
      file={file}
      currentPage={currentPage}
      onPageSelect={goToPage}
      colors={colors}
    />
  );

  return (
    <ExoDialog open={open} onClose={onClose}>
      <Box className="flex h-full w-full">
        {/* Thumbnail Drawer für Mobile */}
        <Drawer
          anchor="left"
          open={sidebarOpen && isMobile}
          onClose={() => setSidebarOpen(false)}
          sx={{ "& .MuiDrawer-paper": { width: 140 } }}
        >
          {thumbnailSidebar}
        </Drawer>

        {/* Thumbnail Sidebar für Desktop */}
        {sidebarOpen && !isMobile && thumbnailSidebar}

        {/* Haupt-PDF-Bereich */}
        <Box
          className="flex flex-col flex-1 h-full w-full overflow-auto"
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          sx={{
            touchAction: "pan-x pan-y",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* Toolbar */}
          <Box className="grid grid-cols-3 left-0 top-0 z-10 sticky gap-2 p-1">
            {/* Linke Seite: Sidebar-Toggle */}
            <Box className="flex items-center">
              <Box
                className="backdrop-blur-lg p-1 rounded-full"
                sx={{ bgcolor: colors.glass }}
              >
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <ViewSidebarIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Mitte: Zoom + Seitennavigation */}
            <Box
              className="w-full flex justify-center items-center gap-1 p-1 backdrop-blur-lg rounded-full flex-wrap"
              sx={{ bgcolor: colors.glass }}
            >
              <IconButton size={isMobile ? "small" : "medium"} onClick={zoomOut}>
                <ZoomOutIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
              <IconButton
                size={isMobile ? "small" : "medium"}
                onClick={resetZoom}
              >
                <FitScreenIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
              <IconButton size={isMobile ? "small" : "medium"} onClick={zoomIn}>
                <ZoomInIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>

              {/* Seitennavigation */}
              {numPages > 1 && (
                <>
                  <Box
                    sx={{
                      width: "1px",
                      height: 24,
                      bgcolor: colors.primary[300],
                      mx: 0.5,
                    }}
                  />
                  <IconButton
                    size={isMobile ? "small" : "medium"}
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    <NavigateBeforeIcon fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                  <TextField
                    value={pageInput}
                    onChange={handlePageInputChange}
                    onKeyDown={handlePageInputSubmit}
                    onBlur={handlePageInputBlur}
                    size="small"
                    inputProps={{
                      style: {
                        textAlign: "center",
                        width: isMobile ? 30 : 40,
                        padding: "4px",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: colors.primary[500],
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mx: 0.5 }}>
                    / {numPages}
                  </Typography>
                  <IconButton
                    size={isMobile ? "small" : "medium"}
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= numPages}
                  >
                    <NavigateNextIcon fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Rechte Seite: Download */}
            <Box className="flex flex-row-reverse">
              <Box
                className="backdrop-blur-lg p-1 rounded-full"
                sx={{ bgcolor: colors.glass }}
              >
                {type === "url" && (
                  <Link href={src} target="_blank">
                    <IconButton size={isMobile ? "small" : "medium"}>
                      <DownloadIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Link>
                )}
              </Box>
            </Box>
          </Box>

          {/* PDF-Dokument */}
          <Box
            sx={{
              minWidth: "fit-content",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Document
              file={file}
              error={
                <Box className="w-full flex flex-col justify-center items-center h-full">
                  <NoDataAnimation size={90} />
                  <Typography>{t("Cant load File")}</Typography>
                </Box>
              }
              onLoadSuccess={onDocumentLoadSuccess}
              options={options}
              loading={
                <Skeleton
                  sx={{
                    padding: 0,
                    margin: 0,
                    height: "100%",
                    transform: "unset",
                    borderRadius: "0px",
                  }}
                />
              }
              className="flex flex-col gap-2 relative"
            >
              {Array.from(new Array(numPages || 0), (el, index) => (
                <Box
                  key={`page_${index + 1}`}
                  ref={(el) => (pageRefs.current[index + 1] = el)}
                  sx={{
                    display: "inline-block",
                  }}
                >
                  <Page
                    className="block rounded-lg overflow-hidden shadow-lg"
                    renderAnnotationLayer={true}
                    renderTextLayer={true}
                    pageNumber={index + 1}
                    width={
                      containerWidth
                        ? Math.min(
                            sidebarOpen && !isMobile
                              ? containerWidth - 120
                              : containerWidth,
                            maxWidth
                          )
                        : maxWidth
                    }
                    scale={scale}
                  />
                </Box>
              ))}
            </Document>
          </Box>
        </Box>
      </Box>
    </ExoDialog>
  );
};
